import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, AppBar, Toolbar, IconButton, useTheme, Dialog, DialogContent, TextField, Button } from '@mui/material';
import ReadingThumbnail from './ReadingThumbnail';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { useSpreadTypes } from '../../constants/readingTypes';
import { useTranslation } from 'react-i18next';
import { fullScreenButtonStyle } from '../../utils/buttonStyles';
interface ReadingCategory {
    type: string;
    setup: number[];
    label: string;
    description: string;
    id: string;
}

interface FavoriteReading {
    type: string;
    count: number;
    setup: number[];
    label: string;
    description: string;
    id: string;
}

const FastReadingCategory: React.FC = () => {
    const [open, setOpen] = useState(false);
    const [selectedReading, setSelectedReading] = useState<ReadingCategory | null>(null);
    const [question, setQuestion] = useState('');
    const navigate = useNavigate();
    const theme = useTheme();
    const { user } = useAuth();
    const [favoriteReadings, setFavoriteReadings] = useState<FavoriteReading[]>([]);
    const { t } = useTranslation();


    useEffect(() => {
        const savedFormData = localStorage.getItem('formData');
        if (savedFormData) {
            const parsedData = JSON.parse(savedFormData);
            setQuestion(parsedData.question || '');
        }

        setFavoriteReadings((user?.favoriteReadings as FavoriteReading[]) || []);
        //console.log('favoriteReadings', user?.favoriteReadings);
    }, [user]);

    const spreadTypes = useSpreadTypes();

    const getReading = (id: string) => {
        // Search through all categories in spreadTypes
        for (const readingTypes of Object.values(spreadTypes)) {
            for (const reading of readingTypes) {

                if (reading.id === id) {
                    console.log('reading', reading.id, reading.description);
                    return reading;
                }
            }
        }
        return null;
    };

    const handleClickOpen = (reading: ReadingCategory) => {
        setSelectedReading(reading);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setQuestion('');
    };

    const handleSubmit = () => {
        if (!selectedReading) return;

        const formData = {
            readingMode: 'in-app',
            question: question,
            notes: '',
            selectedReading: selectedReading
        };
        localStorage.setItem('formData', JSON.stringify(formData));
        localStorage.setItem('readingMetadata', JSON.stringify(formData));

        navigate(`/display-reading/${(selectedReading.id)}`)
        handleClose();
    };

    return (
        <div className="">
            {favoriteReadings.length === 0 ? (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        padding: '20px',
                        background: theme.palette.background.accent,
                        borderRadius: '16px',
                        border: `1px dashed ${theme.palette.accent.main}66`,
                        position: 'relative',
                        overflow: 'hidden',
                        minHeight: '160px',
                        width: '100%',
                        margin: '00px 0px 50px 0px',
                    }}
                >
                    <Typography
                        sx={{
                            color: theme.palette.text.secondary,
                            textAlign: 'center',
                            fontStyle: 'italic'
                        }}
                    >
                        {t('fastReadingCategory.noFavorites')}
                    </Typography>
                </Box>
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        overflowX: 'auto',

                        '&::-webkit-scrollbar': {
                            display: 'none'
                        },
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        scrollSnapType: 'x mandatory',
                        '& > *': {
                            scrollSnapAlign: 'start',
                            width: '160px',
                            height: '180px',
                            flexShrink: 0,
                        },
                        margin: '00px 0px 50px 0px',
                    }}
                >
                    {favoriteReadings.map((reading, index) => (
                    <Paper
                        key={index}
                        onClick={() => handleClickOpen(reading)}
                        sx={{
                            bgcolor: 'white',
                            borderRadius: '8px',
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            cursor: 'pointer',
                            transition: 'transform 0.2s',
                            '&:hover': {
                                transform: 'scale(1.02)',
                                boxShadow: 1
                            },
                            boxShadow: '0px 2px 4px rgba(0,0,0,0.1)'
                        }}
                        elevation={0}
                    >
                        <Typography
                            sx={{
                                fontSize: '14px',
                                color: theme.palette.text.secondary,
                                fontWeight: 'medium',
                                mb: 0,
                                textAlign: 'center',
                                lineHeight: '1',
                                minHeight: '40px', // Changed from fixed height to minHeight
                                maxHeight: '20px', // Added maxHeight for better control
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}
                        >
                            {reading.label}

                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                p: 0,
                                mt: 0,
                            }}
                        >
                            <ReadingThumbnail
                                type={reading.type}
                                setup={reading.setup}
                                title={reading.label}
                                width="100px"
                                height={reading.type === "horseshoe-spread" ? "120px" : "100px"}
                            />
                        </Box>
                    </Paper>
                ))}
                </Box>
            )}

            <SwipeableDrawer
                anchor="bottom"
                open={open}
                onClose={handleClose}
                onOpen={() => { }}
                slotProps={{
                    paper: {
                        sx: {
                            height: '90%',
                            borderTopLeftRadius: '16px',
                            borderTopRightRadius: '16px',
                            padding: '20px',
                            backgroundColor: '#ECEAE5'
                        }
                    }
                }}
            >

                <div className="flex flex-col gap-4">
                    <Typography
                        sx={{
                            width: '100%',
                            textAlign: 'center',
                            fontFamily: '"Playfair Display", serif',
                        }}
                        variant="h1"
                    >
                        {selectedReading?.label}
                    </Typography>
                </div>


                <div className="mt-4 flex flex-col items-center">
                    <ReadingThumbnail
                        type={selectedReading?.type}
                        setup={selectedReading?.setup}
                        width="100px"
                        height={selectedReading?.type === "horseshoe-spread" ? "120px" : "100px"}
                    />
                    <Typography
                        sx={{
                            width: '100%',
                            textAlign: 'center',
                            fontFamily: '"Playfair Display", serif',
                        }}
                    >
                        {selectedReading && getReading(selectedReading.id)?.description}
                    </Typography>

                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('fastReadingCategory.questionLabel')}
                        type="text"
                        fullWidth
                        multiline
                        rows={10}
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        sx={{
                            mt: 4,
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'rgba(255,255,255,0.7)',
                                borderRadius: '12px',
                                border: '1px solid rgba(201, 173, 167, 0.4)',
                                '& fieldset': {
                                    borderColor: 'transparent',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'transparent',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'transparent',
                                },
                            },
                            '& .MuiInputBase-input': {
                                fontFamily: '"Plus Jakarta Sans", sans-serif',
                                fontSize: '1.2rem',
                            },
                        }}
                    />
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        fullWidth
                        sx={fullScreenButtonStyle}
                    >
                        {t('fastReadingCategory.beginReading')}
                    </Button>
                </div>

            </SwipeableDrawer>
        </div>
    );
};

export default FastReadingCategory;
